import { Eye } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { useNow } from '../../../lib/use-now';
import { useCurrentSession } from '../../../stores/session';
import { useStopImpersonation } from '../api/auth-api';
import styles from './ImpersonationBanner.module.css';

/**
 * F1.10 — the banner that must never be missable.
 *
 * It is the whole safety story of impersonation. An HR Lead who forgets they are
 * viewing as somebody else will misread every screen they look at, so this is
 * pinned above the shell, uses the warning tone, and counts down to the automatic
 * end rather than just claiming there is one.
 *
 * The countdown is cosmetic — the server ends the impersonation on its own, when
 * the session is next read. This only makes that visible.
 */
export function ImpersonationBanner(): ReactNode {
  const session = useCurrentSession();
  const stop = useStopImpersonation();

  // The clock as an explicit input, so the countdown is a pure function of it.
  const now = useNow();

  if (!session?.isImpersonating) return null;
  const remaining = remainingMinutes(session.impersonationEndsAt, now);

  return (
    <div className={styles.banner} role="status">
      <Eye size={15} aria-hidden />
      <p className={styles.text}>
        You are viewing as <strong>{session.fullName}</strong>. Nothing can be changed
        {remaining !== null && <> · ends in {remaining} min</>}
      </p>
      <Button
        variant="outline"
        size="sm"
        isPending={stop.isPending}
        onClick={() => stop.mutate()}
      >
        Stop viewing as them
      </Button>
    </div>
  );
}

function remainingMinutes(endsAt: string | undefined, now: number): number | null {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - now;
  if (Number.isNaN(ms) || ms <= 0) return 0;
  return Math.max(1, Math.round(ms / 60_000));
}
